import React from 'react';
import { getImageUrl } from '../config';
import { INDIAN_BANKS, INDIAN_STATES } from './indiaBankKycOptions';
import './SellerBankKycFields.css';

const SellerBankKycFields = ({
  values,
  onChange,
  passbookFile,
  onPassbookChange,
  passbookPreview,
  existingPassbookUrl,
  disabled,
  holderName,
}) => {
  const setField = (name, value) => {
    onChange({ ...values, [name]: value });
  };

  return (
    <div className="seller-bank-kyc">
      <h3 className="seller-bank-kyc-title">Bank KYC</h3>
      <p className="seller-bank-kyc-hint">Same as customer bank KYC. Account holder is the shop owner name.</p>
      {holderName ? (
        <p className="seller-bank-kyc-holder">Account holder: <strong>{holderName}</strong></p>
      ) : (
        <p className="seller-bank-kyc-hint">Enter owner name above so it can be used as the account holder name.</p>
      )}

      <label className="seller-bank-kyc-label">
        Bank Name <span className="req">*</span>
        <select
          value={values.bankName}
          onChange={(e) => onChange({ ...values, bankName: e.target.value, branchName: '' })}
          disabled={disabled}
        >
          <option value="">Select Bank Name</option>
          {!INDIAN_BANKS.includes(values.bankName) && values.bankName ? (
            <option value={values.bankName}>{values.bankName}</option>
          ) : null}
          {INDIAN_BANKS.map((bank) => (
            <option key={bank} value={bank}>{bank}</option>
          ))}
        </select>
      </label>

      <label className="seller-bank-kyc-label">
        Branch State <span className="req">*</span>
        <select
          value={values.branchState}
          onChange={(e) => onChange({ ...values, branchState: e.target.value, branchName: '' })}
          disabled={disabled}
        >
          <option value="">Select Branch State</option>
          {!INDIAN_STATES.includes(values.branchState) && values.branchState ? (
            <option value={values.branchState}>{values.branchState}</option>
          ) : null}
          {INDIAN_STATES.map((state) => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      </label>

      <label className="seller-bank-kyc-label">
        Branch Name <span className="req">*</span>
        <input
          type="text"
          value={values.branchName}
          onChange={(e) => setField('branchName', e.target.value)}
          placeholder="Branch name"
          disabled={disabled || !values.bankName || !values.branchState}
        />
      </label>

      <label className="seller-bank-kyc-label">
        Bank Account Number <span className="req">*</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={values.accountNumber}
          onChange={(e) => setField('accountNumber', e.target.value.replace(/\s/g, ''))}
          placeholder="Enter Bank Account Number"
          disabled={disabled}
        />
      </label>

      <label className="seller-bank-kyc-label">
        Confirm Bank Account Number <span className="req">*</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={values.confirmAccountNumber}
          onChange={(e) => setField('confirmAccountNumber', e.target.value.replace(/\s/g, ''))}
          onPaste={(e) => e.preventDefault()}
          placeholder="Confirm Bank Account Number"
          disabled={disabled}
        />
      </label>

      <label className="seller-bank-kyc-label">
        IFSC Code
        <input
          type="text"
          value={values.ifscCode}
          onChange={(e) => setField('ifscCode', e.target.value.toUpperCase())}
          placeholder="Optional"
          disabled={disabled}
        />
      </label>

      <div className="seller-bank-kyc-label">
        <span>Upload Bank Account Proof (Front Page of Passbook) <span className="req">*</span></span>
        <label className="seller-bank-kyc-dropzone">
          <input
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            onChange={onPassbookChange}
            disabled={disabled}
          />
          {(passbookPreview || existingPassbookUrl) ? (
            <img
              src={passbookPreview || getImageUrl(existingPassbookUrl)}
              alt="Passbook preview"
              className="seller-bank-kyc-preview"
            />
          ) : (
            <>
              <strong>Upload File</strong>
              <em>(jpg, jpeg, png &amp; max 5MB)</em>
            </>
          )}
          {passbookFile ? <span className="seller-bank-kyc-file-name">{passbookFile.name}</span> : null}
        </label>
      </div>
    </div>
  );
};

export const emptySellerBankForm = () => ({
  bankName: '',
  branchState: '',
  branchName: '',
  accountNumber: '',
  confirmAccountNumber: '',
  ifscCode: '',
  accountType: 'Savings',
});

export const isSellerBankFormStarted = (values, passbookFile) =>
  Boolean(
    values.bankName
    || values.branchState
    || values.branchName
    || values.accountNumber
    || values.confirmAccountNumber
    || values.ifscCode
    || passbookFile
  );

export const validateSellerBankForm = (values, passbookFile, hasExistingPassbook) => {
  if (!values.bankName) return 'Bank Name is required.';
  if (!values.branchState) return 'Branch State is required.';
  if (!values.branchName.trim()) return 'Branch Name is required.';
  if (!values.accountNumber.trim()) return 'Bank Account Number is required.';
  if (!values.confirmAccountNumber.trim()) return 'Confirm Bank Account Number is required.';
  if (values.accountNumber.trim() !== values.confirmAccountNumber.trim()) return 'Account numbers do not match.';
  if (!passbookFile && !hasExistingPassbook) return 'Please upload the front page of the passbook.';
  return null;
};

export const appendSellerBankFormData = (formData, values, passbookFile, holderName) => {
  formData.append('accountHolderName', holderName || '');
  formData.append('accountNumber', values.accountNumber.trim());
  formData.append('confirmAccountNumber', values.confirmAccountNumber.trim());
  formData.append('bankName', values.bankName);
  formData.append('branchName', values.branchName.trim());
  formData.append('branchState', values.branchState);
  formData.append('ifscCode', values.ifscCode || '');
  formData.append('accountType', values.accountType || 'Savings');
  if (passbookFile) {
    formData.append('passbookProof', passbookFile);
  }
};

export default SellerBankKycFields;
